/* ═══ AgButton — Anagogia Design System ═══ */

import AgSpinner from '../AgSpinner/AgSpinner';
import styles from './AgButton.module.css';

interface AgButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style of the button */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show a loading spinner and disable interactions */
  loading?: boolean;
  /** Stretch to fill container width */
  fullWidth?: boolean;
  /** Icon rendered before the label */
  startIcon?: React.ReactNode;
  /** Icon rendered after the label */
  endIcon?: React.ReactNode;
  children: React.ReactNode;
}

/** Resolve spinner color based on button variant */
function spinnerColor(
  variant: AgButtonProps['variant'],
): 'white' | 'primary' | 'inherit' {
  if (variant === 'primary' || variant === 'danger') return 'white';
  return 'primary';
}

/**
 * Bouton d'action — variantes primary/secondary/ghost/danger, tailles sm/md/lg, état loading, icônes de début et de fin.
 * @category Boutons
 */
function AgButton({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  children,
  className,
  disabled,
  ...rest
}: AgButtonProps) {
  const cls = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : undefined,
    loading ? styles.loading : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
      {loading && (
        // Le bouton annonce déjà l'attente (`aria-busy`) : le spinner reste muet,
        // sans quoi son nom « Chargement » s'ajouterait à celui du bouton.
        <span className={styles.loadingSpinner} aria-hidden="true">
          <AgSpinner
            size={size === 'lg' ? 'md' : 'sm'}
            color={spinnerColor(variant)}
          />
        </span>
      )}
    </button>
  );
}

export { AgButton };
export type { AgButtonProps };
export default AgButton;
